import { useState, useEffect, useRef } from 'react';
import type { ColumnFiltersState } from '@tanstack/react-table';
import type { FilterOperators, FilterMenuAnchor } from '../components/data-display/types/dataGrid';

type UseFilterOperatorsProps = {
  columnFilters: ColumnFiltersState;
  serverSide: boolean;
  onFilterChange?: (filters: ColumnFiltersState) => void;
};

type UseFilterOperatorsReturn = {
  filterOperators: FilterOperators;
  setFilterOperators: React.Dispatch<React.SetStateAction<FilterOperators>>;
  filterMenuAnchor: FilterMenuAnchor;
  setFilterMenuAnchor: React.Dispatch<React.SetStateAction<FilterMenuAnchor>>;
};

const operatorsFromFilters = (filters: ColumnFiltersState): FilterOperators =>
  filters.reduce<FilterOperators>((operators, filter) => {
    if (typeof filter.value !== 'object' || filter.value === null) return operators;

    const value = filter.value as {
      op?: unknown;
      operator?: unknown;
    };
    const operator = value.op ?? value.operator;
    if (typeof operator === 'string' && operator.length > 0) {
      operators[filter.id] = operator;
    }
    return operators;
  }, {});

/**
 * Hook to manage filter operators state and sync with onFilterChange
 */
export const useFilterOperators = ({
  columnFilters,
  serverSide,
  onFilterChange,
}: UseFilterOperatorsProps): UseFilterOperatorsReturn => {
  const [filterOperators, setFilterOperators] = useState<FilterOperators>(() =>
    operatorsFromFilters(columnFilters)
  );
  const effectiveFilterOperators = {
    ...operatorsFromFilters(columnFilters),
    ...filterOperators,
  };
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<FilterMenuAnchor>({
    element: null,
    columnId: null,
  });

  // Track previous values for change detection
  const prevFiltersRef = useRef<ColumnFiltersState>([]);
  const prevOperatorsRef = useRef<FilterOperators>({});

  // Sync filters with onFilterChange (enrich with operators)
  useEffect(() => {
    if (serverSide && onFilterChange) {
      const filtersChanged =
        JSON.stringify(prevFiltersRef.current) !== JSON.stringify(columnFilters);
      const operatorsChanged =
        JSON.stringify(prevOperatorsRef.current) !== JSON.stringify(filterOperators);

      if (filtersChanged || operatorsChanged) {
        const enrichedFilters = columnFilters.map((filter) => ({
          ...filter,
          value:
            typeof filter.value === 'object' && filter.value !== null
              ? filter.value
              : {
                  operator: filterOperators[filter.id] || 'contains',
                  value: filter.value,
                },
        }));

        prevFiltersRef.current = columnFilters;
        prevOperatorsRef.current = filterOperators;
        onFilterChange(enrichedFilters);
      }
    }
  }, [columnFilters, serverSide, onFilterChange, filterOperators]);

  return {
    filterOperators: effectiveFilterOperators,
    setFilterOperators,
    filterMenuAnchor,
    setFilterMenuAnchor,
  };
};
